<template>
  <vue-final-modal
    :value="visible"
    :name="modalName"
    :classes="['yagi-chat-bot__container']"
    :content-class="[
      'yagi-chat-bot__shell',
      'v--modal-box',
      'v--modal',
      'chat-modal'
    ]"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
  >
    <div class="chat-header">
      <div class="chat-header-content">
        <div class="chat-avatar">
          <img
            src="/static/img/yagichat/yagish_icon.png"
            alt="ヤギコンシェルジュ"
          />
        </div>
        <div class="chat-header-text">
          <div class="chat-title">{{ headerTitle }}</div>
          <div class="chat-subtitle">{{ headerSubtitle }}</div>
        </div>
      </div>
      <button class="close-btn" @click="closeDialog()">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <div v-if="!showResult" class="chat-container">
      <YagiChatBotMessages
        ref="messagesRef"
        :messages="chatMessages"
        :chat-date="chatDate"
        :thinking-mode="thinkingMode"
        :thinking-label="thinkingLabel"
        @edit-skipped="editSkippedQuestions"
        @edit-company="editCompanyInfo"
        @ai-accept="handleAiResultAccept"
        @ai-regenerate="handleAiResultRegenerate"
        @ai-edit="handleAiResultEdit"
      />
      <YagiChatBotInputArea
        :ai-result-mode="aiResultMode"
        :thinking-mode="thinkingMode"
        :is-loading-company-info="isLoadingCompanyInfo"
        :current-question-index="currentQuestionIndex"
        :questions-length="questions.length"
        :all-questions-completed="allQuestionsCompleted"
        :current-question="currentQuestion"
        :current-answer="currentAnswer"
        :show-usage-count="showUsageCount"
        :usage-count="usageCount"
        :max-usage-count="maxUsageCount"
        :usage-count-label="usageCountLabel"
        :complete-button-text="completeButtonText"
        @update:current-answer="currentAnswer = $event"
        @skip-url="skipUrlQuestion"
        @submit-url="submitUrlAnswer"
        @submit="submitAnswer"
        @submit-none="submitNoneAnswer"
        @quick-reply="submitQuickReply"
        @enter="handleEnterKey"
        @composition="isComposing = $event"
        @complete="handleQuestionsComplete"
      />
    </div>

    <div v-if="showResult" class="result-container">
      <slot name="result" :result="result" :close="closeDialog"></slot>
    </div>
  </vue-final-modal>
</template>

<script setup lang="ts">
/**
 * ヤギチャットボット（vue-final-modal）。
 * 開閉・公開 API は defineExpose（親の $refs.open 等を維持）。
 */
import { computed, ref, toRef } from 'vue'
import YagiChatBotInputArea from '@/components/format-edit/yagi-chat-bot/YagiChatBotInputArea.vue'
import YagiChatBotMessages from '@/components/format-edit/yagi-chat-bot/YagiChatBotMessages.vue'
import { useYagiChatBot } from '@/composables/format-edit/useYagiChatBot'
import type {
  YagiChatFormValue,
  YagiChatQuestion
} from '@/utils/format-edit/yagiChatBot'

const props = withDefaults(
  defineProps<{
    modalName: string
    questions: YagiChatQuestion[]
    value: YagiChatFormValue
    autoSkipFilled?: boolean
    showUsageCount?: boolean
    usageCount?: number | null
    maxUsageCount?: number
    usageCountLabel?: string
    chatType?: string
    completeButtonText?: string
    headerTitle?: string
    headerSubtitle?: string
    welcomeMessage?: string
  }>(),
  {
    autoSkipFilled: true,
    showUsageCount: false,
    usageCount: null,
    maxUsageCount: 1,
    usageCountLabel: '利用回数',
    chatType: 'unknown',
    completeButtonText: 'AI文章作成をする',
    headerTitle: 'ヤギコンシェルジュ',
    headerSubtitle: 'ヤギッシュ公式チャットボットです',
    welcomeMessage: ''
  }
)

const emit = defineEmits<{
  (e: 'input', value: YagiChatFormValue): void
  (e: 'complete', value?: YagiChatFormValue): void
  (e: 'close'): void
}>()

const messagesRef = ref<{ rootEl: HTMLElement | null } | null>(null)

const {
  visible,
  currentQuestionIndex,
  chatMessages,
  currentAnswer,
  isComposing,
  showResult,
  result,
  isLoadingCompanyInfo,
  allQuestionsCompleted,
  aiResultMode,
  thinkingMode,
  thinkingLabel,
  currentQuestion,
  chatDate,
  open,
  closeDialog,
  onVisibleInput,
  showResultView,
  showThinkingIndicator,
  hideThinkingIndicator,
  showAiResultConfirm,
  handleAiResultAccept,
  handleAiResultRegenerate,
  handleAiResultEdit,
  resumeFromQuestionId,
  markCompleted,
  pushChatMessage,
  handleEnterKey,
  submitAnswer,
  submitNoneAnswer,
  submitQuickReply,
  editSkippedQuestions,
  handleQuestionsComplete,
  skipUrlQuestion,
  submitUrlAnswer,
  editCompanyInfo
} = useYagiChatBot({
  modalName: toRef(props, 'modalName'),
  questions: toRef(props, 'questions'),
  value: toRef(props, 'value'),
  autoSkipFilled: toRef(props, 'autoSkipFilled'),
  chatType: toRef(props, 'chatType'),
  welcomeMessage: toRef(props, 'welcomeMessage'),
  emitInput: (v) => emit('input', v),
  emitComplete: (v) => emit('complete', v),
  emitClose: () => emit('close'),
  getChatMessagesEl: () => {
    const exposed = messagesRef.value
    if (!exposed) return null
    const el = exposed.rootEl
    // Vue 2.7 expose では Ref のまま渡ることがある
    if (el && typeof el === 'object' && 'value' in el) {
      return (el as { value: HTMLElement | null }).value
    }
    return el ?? null
  }
})

const questions = computed(() => props.questions)

defineExpose({
  open,
  closeDialog,
  showResultView,
  showThinkingIndicator,
  hideThinkingIndicator,
  showAiResultConfirm,
  resumeFromQuestionId,
  markCompleted,
  pushChatMessage,
  chatMessages
})
</script>

<style lang="scss" scoped>
.chat-header {
  background: #004d40;
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .chat-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .chat-avatar {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .chat-header-text {
    .chat-title {
      font-weight: bold;
      font-size: 1rem;
      margin-bottom: 2px;
    }

    .chat-subtitle {
      font-size: 0.75rem;
      opacity: 0.9;
    }
  }

  .close-btn {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }

    i {
      font-size: 1.5rem;
    }
  }
}

.chat-container {
  height: 550px;
  display: flex;
  flex-direction: column;
}

.result-container {
  height: 550px;
  overflow-y: auto;
  padding: 20px;
  background: #fff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #27ae60;
    border-radius: 10px;

    &:hover {
      background: #229954;
    }
  }
}
</style>

<!-- content-class / classes は VFM 内部ノードのため non-scoped -->
<style lang="scss">
@import '@/assets/scss/variables';

.yagi-chat-bot__container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 1001;
}

.yagi-chat-bot__shell.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: 420px;
  max-width: 100%;
  height: auto;
  margin-block: auto;
  overflow: hidden;
  border-radius: 12px;
  padding: 0 !important;
  box-shadow: 0 10px 40px rgb(0 0 0 / 30%);
  left: 0 !important;

  @include max-width(sm) {
    width: 95% !important;
    margin: 0 auto;
  }
}
</style>
